import React, { useState, useEffect, useContext } from "react";
import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import Title from "../../components/Title";
import { makeStyles, Paper, Tabs, Tab } from "@material-ui/core";

import TabPanel from "../../components/TabPanel";

import SchedulesForm from "../../components/SchedulesForm";
import CompaniesManager from "../../components/CompaniesManager";
import PlansManager from "../../components/PlansManager";
import TemplateCreditManager from "../../components/TemplateCreditManager";
import AiCreditManager from "../../components/AiCreditManager";
import HelpsManager from "../../components/HelpsManager";
import Options from "../../components/Settings/Options";
import EmailSettings from "../../components/Settings/EmailSettings";
import CloudApiSettings from "../../components/Settings/CloudApiSettings";
import InstagramSettings from "../../components/Settings/InstagramSettings";
import InstagramWebhookMonitor from "../../components/Settings/InstagramWebhookMonitor";
import ClienteCustomFieldsManager from "../../components/ClienteCustomFieldsManager";
import KanbanSettings from "../../components/Settings/KanbanSettings";
import ClienteFieldRequirementsSettings from "../../components/Settings/ClienteFieldRequirementsSettings";
import OnSpaceSettings from "../../components/Settings/OnSpaceSettings";
import EmpsisSettings from "../../components/Settings/EmpsisSettings";
import SystemAlertsSettings from "../../components/Settings/SystemAlertsSettings";

import { i18n } from "../../translate/i18n.js";
import { toast } from "react-toastify";

import useCompanies from "../../hooks/useCompanies";
import useAuth from "../../hooks/useAuth.js";
import useSettings from "../../hooks/useSettings";
import { AuthContext } from "../../context/Auth/AuthContext";

const useStyles = makeStyles((theme) => ({
  root: {
    flex: 1,
    backgroundColor: theme.palette.background.paper,
  },
  mainPaper: {
    ...theme.scrollbarStyles,
    overflowY: "scroll",
    flex: 1,
  },
  tab: {
    backgroundColor: theme.palette.options,
    borderRadius: 4,
  },
  paper: {
    ...theme.scrollbarStyles,
    overflowY: "scroll",
    padding: theme.spacing(2),
    display: "flex",
    alignItems: "center",
    width: "100%",
  },
  container: {
    width: "100%",
    maxHeight: "100%",
  },
  control: {
    padding: theme.spacing(1),
  },
  textfield: {
    width: "100%",
  },
}));

const Settings = () => {
  const classes = useStyles();
  const [tab, setTab] = useState("options");
  const [schedules, setSchedules] = useState([]);
  const [company, setCompany] = useState({});
  const [loading, setLoading] = useState(false);
  const [settings, setSettings] = useState([]);
  const [schedulesEnabled, setSchedulesEnabled] = useState(false);

  const { find, updateSchedules } = useCompanies();
  const { getAll: getAllSettings } = useSettings();
  const { user } = useContext(AuthContext);
  const isAdmin = String(user?.profile || "").toLowerCase() === "admin";

  // Verifica se o usuário atual é o usuário ID 1
  const canManageCompaniesAndPlans = user && user.id === 1;

  useEffect(() => {
    async function findData() {
      setLoading(true);
      try {
        const companyId = localStorage.getItem("companyId");
        const company = await find(companyId);
        const settingList = await getAllSettings();
        setCompany(company);
        setSchedules(company.schedules);
        setSettings(settingList);

        if (Array.isArray(settingList)) {
          const scheduleType = settingList.find(
            (d) => d.key === "scheduleType"
          );
          if (scheduleType) {
            setSchedulesEnabled(scheduleType.value === "company");
          }
        }

        
      } catch (e) {
        toast.error(e);
      }
      setLoading(false);
    }
    findData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleTabChange = (event, newValue) => {
      async function findData() {
        setLoading(true);
        try {
          const companyId = localStorage.getItem("companyId");
          const company = await find(companyId);
          const settingList = await getAllSettings();
          setCompany(company);
          setSchedules(company.schedules);
          setSettings(settingList);
  
          if (Array.isArray(settingList)) {
            const scheduleType = settingList.find(
              (d) => d.key === "scheduleType"
            );
            if (scheduleType) {
              setSchedulesEnabled(scheduleType.value === "company");
            }
          }
  
          
        } catch (e) {
          toast.error(e);
        }
        setLoading(false);
      }
      findData();
      // eslint-disable-next-line react-hooks/exhaustive-deps

    setTab(newValue);
  };

  const handleSubmitSchedules = async (data) => {
    setLoading(true);
    try {
      setSchedules(data);
      await updateSchedules({ id: company.id, schedules: data });
      toast.success(i18n.t("settings.schedulesUpdated"));
    } catch (e) {
      toast.error(e);
    }
    setLoading(false);
  };

  return (
    <MainContainer className={classes.root}>
      <MainHeader>
        <Title>{i18n.t("settings.title")}</Title>
      </MainHeader>
      <Paper className={classes.mainPaper} elevation={1}>
        <Tabs
          value={tab}
          indicatorColor="primary"
          textColor="primary"
          scrollButtons="on"
          variant="scrollable"
          onChange={handleTabChange}
          className={classes.tab}
        >
          <Tab label={i18n.t("settings.tabs.options")} value={"options"} />
          <Tab label={i18n.t("settings.tabs.kanban")} value={"kanban"} />
          {schedulesEnabled && <Tab label={i18n.t("settings.tabs.schedules")} value={"schedules"} />}
          {canManageCompaniesAndPlans && <Tab label={i18n.t("settings.tabs.companies")} value={"companies"} />}
          {canManageCompaniesAndPlans && <Tab label={i18n.t("settings.tabs.plans")} value={"plans"} />}
          {/* Faturamento saiu daqui: virou aba da tela de Gerenciamento. */}
          {canManageCompaniesAndPlans && <Tab label={i18n.t("settings.tabs.templateCredits")} value={"templateCredits"} />}
          {canManageCompaniesAndPlans && <Tab label={i18n.t("settings.tabs.aiCredits")} value={"aiCredits"} />}
          <Tab label={i18n.t("settings.tabs.email")} value={"email"} />
          <Tab label={i18n.t("settings.tabs.cloudApi")} value={"cloudapi"} />
          <Tab label="Instagram" value={"instagram"} />
          <Tab label={i18n.t("settings.tabs.instagramWebhooks")} value={"instagramWebhooks"} />
          <Tab label={i18n.t("settings.tabs.customFields")} value={"customFields"} />
          {isAdmin && (
            <Tab label={i18n.t("settings.tabs.requiredFields")} value={"requiredFields"} />
          )}
          <Tab label={i18n.t("settings.tabs.help")} value={"helps"} />
          {isAdmin && <Tab label="OnSpace" value={"onspace"} />}
          {isAdmin && <Tab label={i18n.t("settings.tabs.empsis")} value={"empsis"} />}
          {canManageCompaniesAndPlans && (
            <Tab label={i18n.t("settings.tabs.systemAlerts")} value={"systemAlerts"} />
          )}
        </Tabs>
        <Paper className={classes.paper} elevation={0}>
          <TabPanel
            className={classes.container}
            value={tab}
            name={"schedules"}
          >
            <SchedulesForm
              loading={loading}
              onSubmit={handleSubmitSchedules}
              initialValues={schedules}
            />
          </TabPanel>
          
          {canManageCompaniesAndPlans && (
            <TabPanel
              className={classes.container}
              value={tab}
              name={"companies"}
            >
              <CompaniesManager />
            </TabPanel>
          )}

          {canManageCompaniesAndPlans && (
            <TabPanel
              className={classes.container}
              value={tab}
              name={"plans"}
            >
              <PlansManager />
            </TabPanel>
          )}

          {canManageCompaniesAndPlans && (
            <TabPanel
              className={classes.container}
              value={tab}
              name={"templateCredits"}
            >
              <TemplateCreditManager />
            </TabPanel>
          )}

          {canManageCompaniesAndPlans && (
            <TabPanel
              className={classes.container}
              value={tab}
              name={"aiCredits"}
            >
              <AiCreditManager />
            </TabPanel>
          )}

       
              <TabPanel
                className={classes.container}
                value={tab}
                name={"helps"}
              >
                <HelpsManager />
              </TabPanel>

          <TabPanel
            className={classes.container}
            value={tab}
            name={"email"}
          >
            <EmailSettings />
          </TabPanel>

          <TabPanel
            className={classes.container}
            value={tab}
            name={"cloudapi"}
          >
            <CloudApiSettings />
          </TabPanel>

          <TabPanel
            className={classes.container}
            value={tab}
            name={"instagram"}
          >
            <InstagramSettings />
          </TabPanel>

          <TabPanel
            className={classes.container}
            value={tab}
            name={"instagramWebhooks"}
          >
            <InstagramWebhookMonitor />
          </TabPanel>

          <TabPanel
            className={classes.container}
            value={tab}
            name={"customFields"}
          >
            <ClienteCustomFieldsManager />
          </TabPanel>

          {isAdmin && (
            <TabPanel
              className={classes.container}
              value={tab}
              name={"requiredFields"}
            >
              <ClienteFieldRequirementsSettings settings={settings} />
            </TabPanel>
          )}

          <TabPanel className={classes.container} value={tab} name={"options"}>
            <Options
              settings={settings}
              scheduleTypeChanged={(value) =>
                setSchedulesEnabled(value === "company")
              }
            />
          </TabPanel>

          <TabPanel className={classes.container} value={tab} name={"kanban"}>
            <KanbanSettings settings={settings} />
          </TabPanel>

          {isAdmin && (
            <TabPanel className={classes.container} value={tab} name={"onspace"}>
              <OnSpaceSettings settings={settings} />
            </TabPanel>
          )}

          {isAdmin && (
            <TabPanel className={classes.container} value={tab} name={"empsis"}>
              <EmpsisSettings settings={settings} />
            </TabPanel>
          )}

          {canManageCompaniesAndPlans && (
            <TabPanel
              className={classes.container}
              value={tab}
              name={"systemAlerts"}
            >
              <SystemAlertsSettings />
            </TabPanel>
          )}
        </Paper>
      </Paper>
    </MainContainer>
  );
};

export default Settings;
